// The reader's blunders and misses in a game, in game order: the rows of the
// stages' "Blunders and misses" tables (review-stage-moments) and the stops
// of the step controls' outer buttons (review-step-controls), which is why
// it lives here and not in one plugin (moved from review-stage-moments,
// 2026-10-13). Pure: it reads the session's scan through the small interface
// below, which `GameScan` satisfies.
import { type Color, type Fen, type Move, type Uci, type MoveJudgement } from '@chess/api-model';
import { moverOf } from './phases.ts';

export interface MomentScan {
  /** The verdict on the move at a 1-based ply, once the positions around it are read. */
  judgement(ply: number, move: Move | undefined): MoveJudgement | undefined;
  /** Blunder, miss or neither for the move at a 1-based ply, played by `mover` (the review API's rule). */
  kind(ply: number, move: Move | undefined, mover: Color): 'blunder' | 'miss' | undefined;
}

export interface Moment {
  /** 1-based ply of the reader's move; the position it was played from is `ply - 1`. */
  ply: number;
  san: string;
  kind: 'blunder' | 'miss';
  /** Centipawns the move gave up against the engine's best; 5000 or more is a mate. */
  delta: number;
  /** The engine's move from the position before. */
  best: Uci;
}

/** The reader's blunders and misses among the moves `fromPly + 1 … toPly`, each labelled by the review API's rule. */
export function momentsIn(scan: MomentScan | undefined, moves: Move[], fromPly: number, toPly: number, reader: Color | undefined): Moment[] {
  const out: Moment[] = [];
  if (!scan || !reader) return out;
  for (let ply = fromPly + 1; ply <= Math.min(toPly, moves.length); ply++) {
    if (moverOf(ply) !== reader) continue;
    const move = moves[ply - 1];
    const judgement = scan.judgement(ply, move);
    const kind = judgement ? scan.kind(ply, move, reader) : undefined;
    if (!judgement || !kind) continue;
    out.push({ ply, san: move.san, kind, delta: judgement.delta, best: judgement.best });
  }
  return out;
}

/** "7... Nxe4" for a 1-based ply. */
export function momentLabel(m: Pick<Moment, 'ply' | 'san'>): string {
  return `${Math.ceil(m.ply / 2)}${m.ply % 2 === 1 ? '.' : '...'} ${m.san}`;
}

/** What the move cost, in pawns: "−2.1", or "Mate" when it allowed or missed one. */
export function momentCost(m: Pick<Moment, 'delta'>): string {
  if (m.delta >= 5000) return 'Mate';
  return `−${(m.delta / 100).toFixed(1)}`;
}

/** The position a moment's puzzle starts from. */
export const positionOf = (positions: Fen[], m: Pick<Moment, 'ply'>): Fen | undefined => positions[m.ply - 1];
